import { http, HttpResponse } from 'msw';
import { mockSystemUsers, mockRoles, mockAuditLogs } from '../fixtures/system';
import type { UserInfo, RoleConfig } from '@trace/shared/types';

export let usersStore = [...mockSystemUsers];
export let rolesStore = [...mockRoles];
export let logsStore = [...mockAuditLogs];

export const systemHandlers = [
  // 1. List Users (Supports Query params)
  http.get('/api/system/users', ({ request }) => {
    const url = new URL(request.url);
    const keyword = url.searchParams.get('keyword')?.toLowerCase();
    const role = url.searchParams.get('role');
    const status = url.searchParams.get('status');

    let result = [...usersStore];
    if (role && role !== 'all') {
      result = result.filter((u) => u.role === role);
    }
    if (status && status !== 'all') {
      result = result.filter((u) => u.status === status);
    }
    if (keyword) {
      result = result.filter(
        (u) =>
          u.username.toLowerCase().includes(keyword) ||
          u.realName.toLowerCase().includes(keyword) ||
          (u.department && u.department.toLowerCase().includes(keyword)) ||
          (u.phone && u.phone.includes(keyword))
      );
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list: result,
        total: result.length,
        page: 1,
        pageSize: 10,
        totalPages: 1,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // 2. Create User
  http.post('/api/system/users', async ({ request }) => {
    const body = (await request.json()) as Partial<UserInfo>;
    const newUser: UserInfo = {
      id: `usr_${Date.now()}`,
      username: body.username || 'new_user',
      realName: body.realName || '新用户',
      role: body.role || 'manager',
      roleName: body.roleName || (body.role === 'admin' ? '超级管理员' : body.role === 'oem' ? 'OEM厂商' : '生产厂长'),
      email: body.email || 'user@beimi.com',
      phone: body.phone || '13800000000',
      tenantId: body.tenantId || 'tenant_wuchang_001',
      tenantName: body.tenantName || '五常核心示范基地',
      department: body.department || '五常核心有机基地',
      permissions: body.permissions || ['dashboard:view'],
      status: body.status || 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    usersStore = [newUser, ...usersStore];
    return HttpResponse.json({
      code: 200,
      message: '用户账号创建成功',
      data: newUser,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // 3. Update User
  http.put('/api/system/users/:id', async ({ params, request }) => {
    const body = (await request.json()) as Partial<UserInfo>;
    const idx = usersStore.findIndex((u) => u.id === params.id);
    if (idx !== -1) {
      usersStore[idx] = { ...usersStore[idx]!, ...body, updatedAt: new Date().toISOString() };
      return HttpResponse.json({
        code: 200,
        message: '用户信息已更新',
        data: usersStore[idx],
        success: true,
        timestamp: new Date().toISOString(),
      });
    }
    return HttpResponse.json({ code: 404, message: 'User not found', success: false }, { status: 404 });
  }),

  // 4. Update User Status
  http.put('/api/system/users/:id/status', async ({ params, request }) => {
    // 从请求体或 URL 参数读取 status（兼容两种调用方式）
    let status = 'active';
    try {
      const body = (await request.json()) as { status?: string };
      status = body.status || 'active';
    } catch {
      const url = new URL(request.url);
      status = url.searchParams.get('status') || 'active';
    }
    const idx = usersStore.findIndex((u) => u.id === params.id);
    if (idx !== -1) {
      usersStore[idx]!.status = status as 'active' | 'disabled';
      return HttpResponse.json({
        code: 200,
        message: '用户状态已更新',
        data: null,
        success: true,
        timestamp: new Date().toISOString(),
      });
    }
    return HttpResponse.json({ code: 404, message: 'User not found', success: false }, { status: 404 });
  }),

  // 5. Reset User Password
  http.post('/api/system/users/:id/reset-password', ({ params }) => {
    return HttpResponse.json({
      code: 200,
      message: '登录密码已重置为 Password@2026',
      data: null,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // 6. Delete User
  http.delete('/api/system/users/:id', ({ params }) => {
    usersStore = usersStore.filter((u) => u.id !== params.id);
    return HttpResponse.json({
      code: 200,
      message: '用户已成功删除',
      data: null,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // 7. List Roles
  http.get('/api/system/roles', () => {
    const enriched = rolesStore.map((r) => {
      const count = usersStore.filter((u) => u.role === r.role).length;
      return {
        ...r,
        userCount: count,
      };
    });
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: enriched,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // 8. Create Role
  http.post('/api/system/roles', async ({ request }) => {
    const body = (await request.json()) as Partial<RoleConfig>;
    const newRole: any = {
      role: body.role || 'new_role',
      roleName: body.roleName || '自定义业务角色',
      description: body.description || '',
      isSystem: false,
      defaultPath: '/dashboard',
      allowedMenus: ['dashboard'],
    };
    rolesStore.push(newRole);
    return HttpResponse.json({
      code: 200,
      message: '角色创建成功',
      data: newRole,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // 9. Update Role Permissions
  http.put('/api/system/roles/:role', async ({ params, request }) => {
    const body = (await request.json()) as { allowedMenus: string[] };
    const idx = rolesStore.findIndex((r) => r.role === params.role);
    if (idx !== -1) {
      rolesStore[idx] = {
        ...rolesStore[idx]!,
        allowedMenus: body.allowedMenus,
      };
      // 同步更新关联角色的用户 permissions
      usersStore.forEach((u) => {
        if (u.role === params.role) {
          u.permissions = body.allowedMenus;
        }
      });
      return HttpResponse.json({
        code: 200,
        message: '角色权限矩阵配置已更新生效',
        data: rolesStore[idx],
        success: true,
        timestamp: new Date().toISOString(),
      });
    }
    return HttpResponse.json({ code: 404, message: 'Role not found', success: false }, { status: 404 });
  }),

  // 10. Delete Role (系统预置角色不可删除)
  http.delete('/api/system/roles/:role', ({ params }) => {
    const targetRole = rolesStore.find((r) => r.role === params.role);
    if (targetRole && (targetRole as any).isSystem) {
      return HttpResponse.json(
        {
          code: 400,
          message: `系统预置角色【${targetRole.roleName}】不可删除，仅支持修改其权限矩阵配置`,
          success: false,
          timestamp: new Date().toISOString(),
        },
        { status: 400 },
      );
    }
    rolesStore = rolesStore.filter((r) => r.role !== params.role);
    return HttpResponse.json({
      code: 200,
      message: '业务角色已删除',
      data: null,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // 11. List Audit Logs
  http.get('/api/system/logs', () => {
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list: logsStore,
        total: logsStore.length,
        page: 1,
        pageSize: 10,
        totalPages: 1,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),
];
